<!-- 
    This number input is optimized for use in the fdi-shop widget
    If there are additional requirements, consider creating a new component
-->
<template>
    <q-input
        data-qs="number-input"
        type="number"
        dense
        v-model.number="quantity"
        :min="props.min"
        :max="props.max"
        :disable="isDisabled"
        :color="props.warningHighlight ? 'warning' : 'primary'"
        outlined
    />
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";

// Define component props with default values
const props = withDefaults(
    defineProps<{
        min?: number;
        max?: number;
        warningHighlight?: boolean;
        disabled?: boolean;
        initialValue?: number;
    }>(),
    {
        min: 1,
        max: Infinity,
        warningHighlight: false,
        disabled: false,
        initialValue: 1,
    }
);

// Define emit event
const emit = defineEmits(["onChange"]);

// Internal state for the input value
const internalValue = ref<number | null>(props.initialValue);

// Computed property to determine if the input should be disabled
const isDisabled = computed(() => {
    return props.max === 0 || props.disabled;
});

// Writable computed to manage the input value, validation, and events
const quantity = computed({
    get: () => {
        if (isDisabled.value) return 0;
        return internalValue.value;
    },
    set: (newValue: number | string | null | undefined) => {
        if (newValue === null || newValue === undefined || newValue === "") {
            // Allow the input to be temporarily empty
            internalValue.value = null;
            emit("onChange", null); // Inform parent that input is cleared
            return;
        }

        let value = Number(newValue);

        // Clamp the value within the defined min/max bounds
        if (value < props.min) {
            value = props.min;
        } else if (value > props.max) {
            value = props.max;
        }

        internalValue.value = value;
        emit("onChange", value);

        // Force re-render if value was clamped
        if (value !== Number(newValue)) {
            nextTick(() => {
                internalValue.value = value;
            });
        }
    },
});
</script>
